import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { connect } from 'react-redux';
import React, { useState } from 'react';
import { showToast } from 'modules/layout/layout.actions';
import { compose } from 'recompose';
import GooglePlacesAddressAutocomplete from 'modules/intake/form/googlePlacesAddressAutocomplete.component';
import { findCountryId, findProvinceId } from 'modules/common/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const GoogleAddressSearch = ({ address, provinces, countries, classes, onChange }) => {

  const [addressSearchValue, setAddressSearchValue] = useState('');
  const [editedAddress, setEditedAddress] = useState(address ? {
    addressLine1: address.addressLine1 || '',
    city: address.city || '',
    county: address.county || '',
    countryId: address.countryId || '',
    provinceId: address.provinceId || '',
    zipcode: address.zipcode || ''
  } : {
    addressLine1: '',
    city: '',
    county: '',
    countryId: '',
    provinceId: '',
    zipcode: ''
  });

  const handleAddressSelect = (event, details) => {
    if (details) {
      const countryId = findCountryId(details.country, details.countryLongName, countries) || '';
      const provinceId = findProvinceId(details.state, details.stateLongName, provinces) || '';
      
      setEditedAddress({
        addressLine1: details.streetAddress || details.formattedAddress || '',
        city: details.city || '',
        county: details.county || '',
        countryId: countryId,
        provinceId: provinceId,
        zipcode: details.zipcode || ''
      });
      setAddressSearchValue(details.streetAddress || details.formattedAddress || '');
      onChange({
        addressLine1: details.streetAddress || details.formattedAddress || '',
        city: details.city || '',
        county: details.county || '',
        countryId: countryId,
        provinceId: provinceId,
        zipcode: details.zipcode || ''
      });
    }
  };

  return (
    <>
      <dividerLine />
        <div className="row justify-space-between align-center">
            <div className={classes.dialogIconRow}>
            <Tooltip title="Address Information" placement="right">
                <FontAwesomeIcon icon={['fas', 'location-dot']} size="3x" />
            </Tooltip>
            <Typography variant="h5" sx={{ paddingLeft: '20px' }}>
                Address Information
            </Typography>
            </div>
        </div>
        <div className={classes.dialogContentContainer}>
            <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 1, color: 'primary.main', fontWeight: 'bold' }}>
                Quick Address Search
            </Typography>
            <GooglePlacesAddressAutocomplete
                label="Search Address"
                placeholder="Type to search and autofill address fields..."
                initialValue={addressSearchValue}
                onSelect={handleAddressSelect}
                onInputClear={() => setAddressSearchValue('')}
                onInputEdit={(newValue) => setAddressSearchValue(newValue)}
            />
            <Typography variant="caption" display="block" sx={{ marginTop: 0.5, color: 'text.secondary' }}>
                Search for an address to automatically fill the fields below, or fill them manually
            </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <TextField
                label="Address"
                value={editedAddress.addressLine1}
                onChange={(e) => {
                    const newAddress = { ...editedAddress, addressLine1: e.target.value };
                    setEditedAddress(newAddress);
                    onChange(newAddress);
                }}
                fullWidth
                variant="standard"
                />
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <TextField
                label="City"
                value={editedAddress.city}
                onChange={(e) => {
                    const newAddress = { ...editedAddress, city: e.target.value };
                    setEditedAddress(newAddress);
                    onChange(newAddress);
                }}
                fullWidth
                variant="standard"
                />
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <TextField
                label="County"
                value={editedAddress.county}
                onChange={(e) => {
                    const newAddress = { ...editedAddress, county: e.target.value };
                    setEditedAddress(newAddress);
                    onChange(newAddress);
                }}
                fullWidth
                variant="standard"
                />
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <FormControl fullWidth variant="standard">
                <InputLabel>Country</InputLabel>
                <Select
                    label="Country"
                    value={editedAddress.countryId}
                    onChange={(e) => {
                        const newAddress = { ...editedAddress, countryId: e.target.value, provinceId: '' };
                        setEditedAddress(newAddress);
                        onChange(newAddress);
                    }}
                >
                    {countries && [...countries].sort((a, b) => {
                      if (a.title === 'United States') return -1;
                      if (b.title === 'United States') return 1;
                      return a.title.localeCompare(b.title);
                    }).map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                        {opt.title}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <FormControl fullWidth variant="standard">
                <InputLabel>State</InputLabel>
                <Select
                    label="State"
                    value={editedAddress.provinceId}
                    disabled={!editedAddress.countryId}
                    onChange={(e) => {
                        const newAddress = { ...editedAddress, provinceId: e.target.value };
                        setEditedAddress(newAddress);
                        onChange(newAddress);
                    }}
                >
                    {editedAddress.countryId && provinces && [...provinces]
                      .filter((pe) => pe.countryId === editedAddress.countryId)
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map(opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.title}
                        </MenuItem>
                    ))}
                </Select>
                </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <TextField
                label="Zip Code"
                value={editedAddress.zipcode}
                onChange={(e) => {
                    const newAddress = { ...editedAddress, zipcode: e.target.value };
                    setEditedAddress(newAddress);
                    onChange(newAddress);
                }}
                fullWidth
                variant="standard"
                />
            </Box>
            </Box>
        </div>
    </>
  );
};

export default compose(connect(null, { showToast }))(GoogleAddressSearch);
